import { BROWN, NEUTRAL, ORANGE, PLUM, VIOLET } from "../../styles/brandTokens";
import { RESOLVER_STATE_COLORS } from "../../shared/components/resolverStateStyle";
import type { TheoOverview, TheoOverviewActionSource, TheoOverviewTechnician, TimeSavedModel } from "../../types/integration";
import { SUMMARY_WINDOWS } from "../theoAutomationsMonitorFilters";

const TOP_ROWS = 10;
const MINUTES_PER_HOUR = 60;

export interface OverviewFilters {
  window: string;
  /** Agent slug, or "" for every agent. */
  agent: string;
  /** Horizon customer id, or "" for every customer. */
  customer: string;
}

export interface BarSegment {
  key: string;
  label: string;
  count: number;
  color: string;
  /** Primary segments stay in the legend at zero; the rest appear only when present. */
  primary: boolean;
  /** Replaces the formatted count in the legend, e.g. "26 h". */
  valueLabel?: string;
  /** Muted text after the label, e.g. "328 actions". */
  detail?: string;
}

/** Agents are violet (Theo); technicians' own work is the brown people family; unattended runs a quiet plum. */
export const SOURCE_COLORS: Record<TheoOverviewActionSource, string> = {
  agents: VIOLET.main,
  ticket_chat: BROWN.main,
  chat_assistant: BROWN.soft,
  automations: PLUM.muted,
};

const STATE_SEGMENTS = [
  { state: "success", label: "completed", color: RESOLVER_STATE_COLORS.success.dot, primary: true },
  { state: "waiting", label: "waiting", color: ORANGE.main, primary: true },
  { state: "running", label: "in progress", color: VIOLET.main, primary: false },
  { state: "cancelled", label: "cancelled", color: NEUTRAL.borderStrong, primary: false },
  { state: "declined", label: "not attempted", color: NEUTRAL.border, primary: false },
] as const;

const WINDOW_VALUES = new Set(SUMMARY_WINDOWS.map((option) => option.value));
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function readOverviewFilters(search: URLSearchParams): OverviewFilters {
  const window = search.get("window") || "";
  const agent = (search.get("agent") || "").trim();
  const customer = (search.get("customer") || "").trim();
  return {
    window: WINDOW_VALUES.has(window) ? window : "all",
    agent: SLUG_PATTERN.test(agent) ? agent : "",
    customer: /^\d+$/.test(customer) ? customer : "",
  };
}

/** A new query string with `patch` applied; defaults are left out so the URL stays short. */
export function writeOverviewFilters(current: URLSearchParams, patch: Partial<OverviewFilters>): URLSearchParams {
  const next = new URLSearchParams(current);
  const merged = { ...readOverviewFilters(current), ...patch };
  const entries: [keyof OverviewFilters, string, string][] = [
    ["window", merged.window, "all"],
    ["agent", merged.agent, ""],
    ["customer", merged.customer, ""],
  ];
  for (const [key, value, fallback] of entries) {
    if (!value || value === fallback) next.delete(key);
    else next.set(key, value);
  }
  return next;
}

export function hasActiveSlice(filters: OverviewFilters): boolean {
  return Boolean(filters.agent || filters.customer);
}

/** Compact time for legends and lists: "45 min", "26 h", "1,204 h". */
export function formatHours(minutes: number): string {
  if (minutes < MINUTES_PER_HOUR) return `${Math.round(minutes)} min`;
  return `${Math.round(minutes / MINUTES_PER_HOUR).toLocaleString()} h`;
}

/** The headline figure, split so the number can be set large and the unit small. */
export function timeSavedHeadline(minutes: number, hoursPerDay = 8): { value: string; unit: string; days: string | null } {
  if (minutes < MINUTES_PER_HOUR) {
    const rounded = Math.round(minutes);
    return { value: rounded.toLocaleString(), unit: rounded === 1 ? "minute" : "minutes", days: null };
  }
  const hours = Math.round(minutes / MINUTES_PER_HOUR);
  const days = Math.round(minutes / MINUTES_PER_HOUR / hoursPerDay);
  return {
    value: hours.toLocaleString(),
    unit: hours === 1 ? "hour" : "hours",
    days: days >= 1 ? `${days.toLocaleString()} technician ${days === 1 ? "day" : "days"}` : null,
  };
}

const plural = (count: number, one: string, many: string) => `${count.toLocaleString()} ${count === 1 ? one : many}`;

export function stateSegments(overview: TheoOverview): BarSegment[] {
  const counts = new Map(overview.agent_tickets.by_state.map((row) => [row.resolver_state, row.count]));
  return STATE_SEGMENTS.map((segment) => ({
    key: segment.state,
    label: segment.label,
    color: segment.color,
    primary: segment.primary,
    count: counts.get(segment.state) || 0,
  }));
}

/** Time saved by who ran the work; the bar is weighted by minutes, not action count. */
export function timeSavedSegments(overview: TheoOverview): BarSegment[] {
  return (overview.actions?.by_source || []).map((row) => ({
    key: row.source,
    label: row.label,
    count: row.estimated_minutes,
    color: SOURCE_COLORS[row.source] || NEUTRAL.borderStrong,
    primary: row.source !== "automations",
    valueLabel: formatHours(row.estimated_minutes),
    detail: plural(row.count, "action", "actions"),
  }));
}

export interface AssistedView {
  kind: "hidden" | "error" | "count";
  tickets: number;
  conversations: number;
  technicians: number;
}

export function assistedView(overview: TheoOverview): AssistedView {
  const assisted = overview.assisted;
  const none = { tickets: 0, conversations: 0, technicians: 0 };
  if (!assisted) return { kind: "hidden", ...none };
  if ("error" in assisted) return { kind: "error", ...none };
  return { kind: "count", ...assisted };
}

export interface OverviewTopRow {
  key: string;
  label: string;
  count: number;
  valueLabel?: string;
  detail?: string;
  segments?: { key: string; count: number; color: string }[];
}

export function ticketTypeRows(overview: TheoOverview, splitBySource: boolean): OverviewTopRow[] {
  return overview.top_ticket_types.slice(0, TOP_ROWS).map((row) => ({
    key: row.ticket_type || "__none__",
    label: row.ticket_type || "No ticket type",
    count: row.total,
    segments: splitBySource
      ? [
          { key: "agents", count: row.agents, color: SOURCE_COLORS.agents },
          { key: "technicians", count: row.technicians, color: SOURCE_COLORS.ticket_chat },
        ]
      : undefined,
  }));
}

/** Action types ranked by the time they saved, with the action count as the supporting detail. */
export function timeSavedByActionRows(overview: TheoOverview): OverviewTopRow[] {
  return [...(overview.actions?.by_category || [])]
    .sort((a, b) => b.estimated_minutes - a.estimated_minutes)
    .slice(0, TOP_ROWS)
    .map((row) => ({
      key: row.category,
      label: row.label,
      count: row.estimated_minutes,
      valueLabel: formatHours(row.estimated_minutes),
      detail: plural(row.count, "action", "actions"),
    }));
}

export interface WorkRow extends OverviewTopRow {
  kind: "agent" | "technician";
  /** The agent's ID as a display key, or the technician's email. */
  id: string;
}

/**
 * Agents and technicians in one ranking, by tickets worked and then actions run, so their work
 * can be compared side by side. A technician's tickets are the ones they prompted Theo on.
 */
export function workRows(overview: TheoOverview): WorkRow[] {
  const workers = [
    ...overview.agent_tickets.by_agent.map((row) => ({
      kind: "agent" as const,
      id: String(row.agent_id),
      label: row.name,
      tickets: row.total,
      actions: row.actions,
    })),
    ...assistedTechnicians(overview).map((person) => ({
      kind: "technician" as const,
      id: person.email,
      label: person.email,
      tickets: person.tickets,
      actions: person.actions as number | null,
    })),
  ];
  return workers
    .sort((a, b) => b.tickets - a.tickets || (b.actions ?? 0) - (a.actions ?? 0) || a.label.localeCompare(b.label))
    .slice(0, TOP_ROWS)
    .map((worker) => ({
      kind: worker.kind,
      id: worker.id,
      key: `${worker.kind}:${worker.id}`,
      label: worker.label,
      count: worker.tickets,
      valueLabel: plural(worker.tickets, "ticket", "tickets"),
      detail: worker.actions === null ? undefined : plural(worker.actions, "action", "actions"),
      segments: [
        {
          key: worker.kind,
          count: worker.tickets,
          color: worker.kind === "agent" ? SOURCE_COLORS.agents : SOURCE_COLORS.ticket_chat,
        },
      ],
    }));
}

export interface MethodologyRow {
  category: string;
  label: string;
  covers: string;
  example: string;
  minutesPerAction: number;
  /** This view's actions in the category, and the time they account for. */
  actions: number;
  minutes: number;
}

/** The published mapping, with this view's actions laid against it so the total can be checked by hand. */
export function methodologyRows(model: TimeSavedModel, overview: TheoOverview | null): MethodologyRow[] {
  const inView = new Map((overview?.actions?.by_category || []).map((row) => [row.category, row]));
  return model.categories.map((row) => ({
    category: row.category,
    label: row.label,
    covers: row.covers,
    example: row.example,
    minutesPerAction: row.minutes_per_action,
    actions: inView.get(row.category)?.count || 0,
    minutes: inView.get(row.category)?.estimated_minutes || 0,
  }));
}

/** Says which slice a list belongs to, e.g. "Last 30 days · Contoso" or "All time · all customers". */
export function scopeLabel(filters: OverviewFilters, customerName: string | null): string {
  const window = SUMMARY_WINDOWS.find((option) => option.value === filters.window)?.label || "All time";
  const customer = filters.customer ? customerName || "Selected customer" : "all customers";
  return `${window} · ${customer}`;
}

export function assistedTechnicians(overview: TheoOverview | null): TheoOverviewTechnician[] {
  const assisted = overview?.assisted;
  return assisted && !("error" in assisted) ? assisted.people : [];
}

export type EmptyStateKind = "new" | "filtered";

/**
 * Null when the view has something to show. A view is empty only when every part loaded and is
 * zero; a part that failed to load is an error to report, never "no work". "new" is an MSP with
 * nothing yet (all time, no filters); "filtered" means the filters narrowed everything away.
 */
export function emptyStateKind(overview: TheoOverview, filters: OverviewFilters): EmptyStateKind | null {
  const { assisted, actions } = overview;
  if (!actions || (assisted && "error" in assisted)) return null;
  const assistedWork = assisted ? assisted.tickets + assisted.conversations : 0;
  if (overview.agent_tickets.total > 0 || actions.total > 0 || assistedWork > 0) return null;
  return filters.window === "all" && !hasActiveSlice(filters) ? "new" : "filtered";
}

/** The resolver state as the agents bar names and colours it. */
export function resolverStateStyle(state: string): { label: string; color: string } {
  const segment = STATE_SEGMENTS.find((row) => row.state === state);
  return segment ? { label: segment.label, color: segment.color } : { label: state || "unknown", color: NEUTRAL.borderStrong };
}

/** Says when a source list shows only the most recent rows, e.g. "Showing the 500 most recent of 612." */
export function truncationNote(list: { total: number; rows: unknown[] }): string | null {
  if (list.total <= list.rows.length) return null;
  return `Showing the ${list.rows.length.toLocaleString()} most recent of ${list.total.toLocaleString()}.`;
}

/** Whether this viewer may open the lists that name technicians; older servers imply it from the people list. */
export function canListPeople(overview: TheoOverview | null): boolean {
  if (!overview) return false;
  return overview.can_list_people ?? assistedTechnicians(overview).length > 0;
}

/** The slice a source list belongs to, with the agent when one is selected. */
export function sourceScopeLabel(filters: OverviewFilters, customerName: string | null, agentName: string | null): string {
  const base = scopeLabel(filters, customerName);
  return agentName ? `${base} · ${agentName}` : base;
}
